<script setup lang="ts">
import ArticleNewObject from '~/entities/article/ui/ArticleNewObject.vue';
import { OG_TYPE } from '../lib/seo';
import {
  OG_SITE_NAME,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  getAbsoluteUrl,
  getOgImageMeta,
} from '~/shared/lib/seo';
import {
  CLIENT_API_NEWS_MATERIALS_KEY,
  CLIENT_API_NEWS_MATERIALS_URL,
  CLIENT_NEWS_BY_AUTHOR_URL,
} from '~/shared/api/client/news/news.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import type { MaterialDTO } from '~/shared/api/client/news/news.dto';
import type { ExpertNewsDTO } from '~/shared/api/client/expert-news/expert-news.dto';
import { isMaterialDTO } from '~/shared/lib/isMaterialDTO';
import OgImage from '~/shared/images/og-image.png';
import { getAuthorName } from '~/shared/lib/getAuthorName';
import { useOrigin } from '~/shared/lib/useOrigin';

const route = useRoute();

const newsId = ref<number | undefined>(Number(route.params.id));

if (!newsId.value) {
  throw createError({
    status: 404,
    message: 'Материал не найден',
    fatal: true,
  });
}

const updateSeo = (data: MaterialDTO | ExpertNewsDTO) => {
  const origin = useOrigin();
  const url = import.meta.client
    ? origin + window.location.pathname
    : origin + useRouter().currentRoute.value.fullPath;

  const title = data?.title ?? 'Новость';
  const description = isMaterialDTO(data) ? data.subject : data.text;
  const image =
    getAbsoluteUrl(origin, data?.image?.photo?.file_url) ??
    getAbsoluteUrl(origin, OgImage);
  const breadcrumbs = isMaterialDTO(data)
    ? [
        data.category
          ? { id: data.category.id, name: data.category.name }
          : undefined,
        ...data.sectors.map((sector) => ({ id: sector.id, name: sector.name })),
      ].filter(
        (value, index, source): value is { id: number; name: string } => {
          if (!value) return false;

          return source.findIndex((item) => item?.id === value.id) === index;
        },
      )
    : undefined;

  const jsonLdGraph = [
    {
      '@type': 'Article',
      headline: title,
      description: description,
      image: image,
      datePublished: data?.publication_date,
      dateModified: data?.updated_datetime,
      author: {
        '@type': 'Person',
        name: isMaterialDTO(data)
          ? getAuthorName(data?.author)
          : (data as ExpertNewsDTO)?.person?.fio || undefined,
        url:
          isMaterialDTO(data) && data?.author
            ? `${CLIENT_NEWS_BY_AUTHOR_URL}${data?.author?.id}`
            : undefined,
      },
      publisher: {
        '@type': 'Organization',
        name: OG_SITE_NAME,
        logo: {
          '@type': 'ImageObject',
          url: getAbsoluteUrl(origin, OgImage),
          width: OG_IMAGE_WIDTH,
          height: OG_IMAGE_HEIGHT,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    },
    ...(breadcrumbs?.length
      ? [
          {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.name,
              item: `${origin}/category/${item.id}`,
            })),
          },
        ]
      : []),
  ];

  const jsonLdScripts = [
    {
      type: 'application/ld+json',
      key: 'ld-json',
      innerHTML: {
        '@context': 'https://schema.org',
        '@graph': jsonLdGraph,
      },
    },
  ];

  useHead({
    title: title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:type', content: OG_TYPE },
      { property: 'og:url', content: url },
      {
        property: 'article:published_time',
        content: data?.publication_date,
      },
      {
        property: 'article:modified_time',
        content: data?.updated_datetime,
      },
      { property: 'og:title', content: title },
      {
        property: 'og:description',
        content: description,
      },
      { property: 'og:site_name', content: OG_SITE_NAME },
      ...getOgImageMeta(origin, data?.image?.photo?.file_url),
    ],
    script: jsonLdScripts,
  });
};

const { status, data } = await useCustomFetch<MaterialDTO>(
  () => `${CLIENT_API_NEWS_MATERIALS_URL}${newsId.value}/?type=new_object`,
  {
    method: 'GET',
    key: `${CLIENT_API_NEWS_MATERIALS_KEY}-${newsId.value}`,
  },
);

if (status.value === 'error') {
  throw createError({
    status: 404,
    message: 'Материал не найден',
    fatal: true,
  });
}

if (data.value?.redirect && status.value === 'success') {
  navigateTo(data.value.redirect, { redirectCode: 301, external: true });
}

if (data.value && status.value === 'success') {
  updateSeo(data.value);
}
</script>
<template>
  <div>
    <template v-if="data && status === 'success'">
      <ArticleNewObject v-bind="data" />
    </template>
    <div
      v-else-if="
        status === 'error' || (status === 'success' && data && !('id' in data))
      "
      :class="$style.error"
    >
      <p>Не удалось получить новость</p>
      <p>Попробуйте еще раз</p>
    </div>
    <template v-else-if="!data && status === 'pending'">
      <Loader />
    </template>
  </div>
</template>

<style lang="scss" module>
.error {
  text-align: center;
  padding-top: 20px;
  p {
    margin: 10px 0;
  }
}

@media (max-width: 767px) {
  .article {
    + .article {
      margin-top: var(--space-3x);
    }
  }
}

@media (max-width: 479px) {
  .article {
    padding-top: var(--space-2x);
    padding-bottom: var(--space-2x);
  }
}
</style>
